import { Component, computed, DestroyRef, inject, input, linkedSignal, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { LucideModule } from '../../../../../shared/modules/lucide.module';
import { salvarArquivo } from '../../../../../shared/utils/arquivo.util';
import { PedidoSicPublicoResponse, paraSolicitacaoSic } from '../../../models/pedido-sic-acompanhamento.model';
import { SolicitacaoPublicaResponse, statusParaAcompanhamento } from '../../../models/solicitacao-servico.model';
import {
  mensagemErroDownloadAnexo,
  paraSolicitacaoAcompanhamento,
  SolicitacaoServicoPublicaService,
} from '../../../services/solicitacao-servico-publica.service';
import {
  AnexoRespostaSolicitacao,
  CATEGORIAS_SOLICITACAO,
  EventoHistorico,
  formatarData,
  Solicitacao,
  STATUS_SOLICITACAO,
  StatusSolicitacao,
} from '../../../services/solicitacoes.service';
import { ComplementoPedidoSicComponent } from './complemento-pedido-sic/complemento-pedido-sic.component';
import { ComplementoSolicitacaoComponent } from './complemento-solicitacao/complemento-solicitacao.component';

/** Uma resposta do órgão pronta para o template: listas sempre presentes e o tom do status. */
interface RespostaExibida {
  /** Id do título, para o `aria-labelledby` do bloco. */
  idTitulo: string;
  respondidaEm: string;
  paragrafos: readonly string[];
  status: string;
  /** O status do domínio no vocabulário da tela, só para reaproveitar as cores do resumo. */
  tomStatus: StatusSolicitacao | null;
  links: readonly string[];
  formularios: readonly string[];
  anexos: readonly AnexoRespostaSolicitacao[];
  /** Só no e-SIC: o tipo da resposta da LAI e o fundamento legal. */
  tipo: string;
  fundamento: string;
}

/** A falha do último download, presa ao anexo que falhou. */
interface ErroAnexo {
  codigo: number;
  mensagem: string;
}

/**
 * Contador das instâncias: o detalhe pode estar na página e no modal ao mesmo tempo, e os ids
 * dos títulos das respostas não podem colidir.
 */
let sequencia = 0;

@Component({
  selector: 'app-detalhe-solicitacao',
  templateUrl: './detalhe-solicitacao.component.html',
  styleUrls: ['./detalhe-solicitacao.component.css'],
  imports: [LucideModule, ComplementoSolicitacaoComponent, ComplementoPedidoSicComponent, RouterLink],
})
export class DetalheSolicitacaoComponent {
  private readonly solicitacaoServicoPublica = inject(SolicitacaoServicoPublicaService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly prefixoId = `detalhe-solicitacao-${++sequencia}`;

  readonly solicitacao = input.required<Solicitacao>();

  /** A solicitação depois do complemento, já no modelo da tela. Quem a guarda é a página. */
  readonly atualizada = output<Solicitacao>();

  private readonly protocoloAtual = computed(() => this.solicitacao().protocolo);

  /**
   * O aviso de complemento enviado. Sobrevive à troca dos dados do mesmo protocolo — é essa
   * troca que tira o card — e some quando outro protocolo entra no detalhe.
   */
  readonly avisoComplemento = linkedSignal<string, string>({
    source: this.protocoloAtual,
    computation: () => '',
  });

  readonly dataEnvio = computed(() => formatarData(this.solicitacao().abertaEm));

  readonly categoria = computed(() =>
    CATEGORIAS_SOLICITACAO.find((opcao) => opcao.id === this.solicitacao().categoria)
  );

  readonly rotuloCategoria = computed(() => this.categoria()?.rotulo ?? '');

  readonly iconeCategoria = computed(() => this.categoria()?.icone ?? 'tag');

  /** No e-SIC, o rótulo do back-end (um dos dez); na Carta de Serviço, o grupo de tela. */
  readonly rotuloStatus = computed(
    () =>
      this.solicitacao().sic?.statusDescricao ??
      STATUS_SOLICITACAO.find((opcao) => opcao.id === this.solicitacao().status)?.rotulo ??
      ''
  );

  /** O título de cada resposta, pela CATEGORIA do item. */
  readonly tituloResposta = computed(() =>
    this.solicitacao().categoria === 'sic' ? 'Resposta do e-SIC' : 'Resposta do órgão'
  );

   readonly linhaDoTempo = computed<readonly EventoHistorico[]>(() =>
    [...this.solicitacao().historico].reverse()
  );

  /**
   * As respostas do órgão, da mais **recente** para a mais antiga — a API e o modelo as trazem
   * na ordem inversa, como o histórico. Vazio fora da solicitação de serviço: então o detalhe
   * mostra o "Parecer de Conclusão Oficial" dos mocks de Ouvidoria e SIC, como sempre.
   */
  readonly respostas = computed<readonly RespostaExibida[]>(() =>
    [...(this.solicitacao().respostas ?? [])].reverse().map((resposta, indice) => ({
      idTitulo: `${this.prefixoId}-resposta-${indice}`,
      respondidaEm: resposta.respondidaEm,
      paragrafos: resposta.paragrafos,
      status: resposta.status ?? '',
      tomStatus: resposta.status ? statusParaAcompanhamento(resposta.status) : null,
      links: resposta.links ?? [],
      formularios: resposta.formularios ?? [],
      anexos: resposta.anexos ?? [],
      tipo: resposta.tipo ?? '',
      fundamento: resposta.fundamento ?? '',
    }))
  );

  /** O código do anexo sendo baixado. Um por vez: os outros "Baixar" esperam. */
  readonly baixando = signal<number | null>(null);

  /** Zera quando outra solicitação entra no detalhe — o aviso é do anexo que falhou, não da tela. */
  readonly erroAnexo = linkedSignal<Solicitacao, ErroAnexo | null>({
    source: this.solicitacao,
    computation: () => null,
  });

  /**
   * Baixa o anexo pela rota pública do protocolo, como `blob`, e o salva com o nome original.
   * A falha vira um aviso junto do arquivo (o erro HTTP é local, sem o modal global).
   */
  baixarAnexo(anexo: AnexoRespostaSolicitacao): void {
    if (this.baixando() !== null) return;

    this.baixando.set(anexo.codigo);
    this.erroAnexo.set(null);

    this.solicitacaoServicoPublica
      .baixarAnexoResposta(this.solicitacao().protocolo, anexo.codigo)
      .pipe(
        finalize(() => this.baixando.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (arquivo) => salvarArquivo(arquivo, anexo.nome),
        error: (erro: unknown) =>
          this.erroAnexo.set({ codigo: anexo.codigo, mensagem: mensagemErroDownloadAnexo(erro) }),
      });
  }

  /** A resposta ao complemento do e-SIC sempre retoma o prazo, que estava suspenso. */
  aoEnviarComplementoSic(resposta: PedidoSicPublicoResponse): void {
    this.avisoComplemento.set('Complemento enviado. O prazo voltou a contar.');
    this.atualizada.emit(paraSolicitacaoSic(resposta));
  }

  /** Só promete o prazo correndo de novo quando o pedido do órgão o tinha pausado. */
  aoEnviarComplemento(resposta: SolicitacaoPublicaResponse, pausaPrazo: boolean): void {
    this.avisoComplemento.set(
      pausaPrazo
        ? 'Complemento enviado. O órgão foi avisado e o prazo voltou a contar.'
        : 'Complemento enviado. O órgão foi avisado.',
    );
    this.atualizada.emit(paraSolicitacaoAcompanhamento(resposta));
  }
}
